import React from "react";
import { cn } from "@/lib/utils";

export const TONES = {
  lime: "bg-mk-lime/15 text-mk-lime border-mk-lime/30",
  blue: "bg-mk-blue/20 text-[#7fb0ff] border-mk-blue/40",
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  red: "bg-red-500/15 text-red-300 border-red-500/30",
  dim: "bg-mk-raised text-mk-dim border-mk-border",
};

export const Pill = ({ tone = "dim", children, className, mono, ...rest }) => (
  <span {...rest} className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-none whitespace-nowrap", mono && "font-mono", TONES[tone], className)}>
    {children}
  </span>
);

export const Avatar = ({ initials, tone = "blue", size = "h-6 w-6 text-[10px]" }) => (
  <span className={cn("grid place-items-center rounded-full font-semibold shrink-0", size, tone === "lime" ? "bg-mk-lime text-mk-bg" : tone === "blue" ? "bg-mk-blue text-white" : "bg-mk-raised text-mk-text")}>
    {initials}
  </span>
);

export const Mono = ({ children, className }) => <span className={cn("font-mono text-mk-lime", className)}>{children}</span>;

export const MiniStat = ({ label, value, tone = "text-mk-text", sub }) => (
  <div className="rounded-lg border border-mk-border bg-mk-bg/60 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-mk-muted">{label}</div>
    <div className={cn("font-display text-lg font-bold leading-tight", tone)}>{value}</div>
    {sub && <div className="text-[10px] text-mk-muted">{sub}</div>}
  </div>
);

export const Bar = ({ pct, tone = "bg-mk-lime", className, delay = 0 }) => (
  <div className={cn("h-1.5 w-full rounded-full bg-mk-raised overflow-hidden", className)}>
    <div className={cn("h-full rounded-full bar-grow", tone)} style={{ width: `${pct}%`, animationDelay: `${delay}ms` }} />
  </div>
);

export const Btn = ({ children, tone = "lime", className, ...rest }) => (
  <span {...rest} className={cn("inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-semibold", tone === "lime" ? "bg-mk-lime text-mk-bg" : tone === "blue" ? "bg-mk-blue text-white" : "border border-mk-strong text-mk-text bg-mk-raised", className)}>
    {children}
  </span>
);

export const BrowserFrame = ({ url = "app.fixlr.app", children, className, live = true, bodyClass }) => (
  <div className={cn("min-w-0 max-w-full rounded-2xl border border-mk-strong/60 bg-mk-surface shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] overflow-hidden", className)}>
    <div className="flex items-center gap-3 border-b border-mk-border bg-mk-bg/80 px-4 py-2.5 min-w-0">
      <div className="flex gap-1.5 shrink-0">
        <span className="chrome-dot bg-[#ff5f57]" />
        <span className="chrome-dot bg-[#febc2e]" />
        <span className="chrome-dot bg-[#28c840]" />
      </div>
      <div className="flex-1 min-w-0 flex items-center justify-center">
        <div className="flex items-center gap-2 min-w-0 max-w-full rounded-md border border-mk-border bg-mk-surface px-3 py-1 font-mono text-[11px] text-mk-dim">
          <span className="text-mk-muted hidden sm:inline">https://</span><span className="truncate">{url}</span>
          {live && (
            <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] text-mk-lime shrink-0"><span className="live-dot" />LIVE</span>
          )}
        </div>
      </div>
      <div className="w-12 hidden sm:block shrink-0" />
    </div>
    <div className={cn("relative p-4 sm:p-5 text-mk-text overflow-x-auto", bodyClass)}>{children}</div>
  </div>
);

export const PhoneFrame = ({ children, time = "09:42", className }) => (
  <div className={cn("relative mx-auto w-[240px] rounded-[2.2rem] border-[6px] border-[#2b2c25] bg-mk-bg shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] overflow-hidden", className)}>
    <div className="absolute left-1/2 top-2 -translate-x-1/2 h-4 w-20 rounded-full bg-[#2b2c25]" />
    <div className="flex items-center justify-between px-5 pt-3 pb-1 font-mono text-[10px] text-mk-dim">
      <span>{time}</span>
      <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-mk-dim" /><span className="inline-block h-2 w-4 rounded-sm bg-mk-lime" /></span>
    </div>
    <div className="px-3 pb-4 text-mk-text">{children}</div>
  </div>
);

export const TableHead = ({ cols }) => (
  <div className="grid gap-2 border-b border-mk-border px-3 py-2 text-[10px] uppercase tracking-wider text-mk-muted" style={{ gridTemplateColumns: cols }}>
    {Array.isArray(cols) ? null : null}
  </div>
);

export const Panel = ({ title, right, children, className }) => (
  <div className={cn("rounded-xl border border-mk-border bg-mk-surface", className)}>
    {(title || right) && (
      <div className="flex items-center justify-between border-b border-mk-border px-3 py-2">
        <div className="text-[11px] font-semibold text-mk-text">{title}</div>
        <div className="text-[10px] text-mk-muted">{right}</div>
      </div>
    )}
    <div className="p-3">{children}</div>
  </div>
);
